import { useState, useEffect, useRef } from "react";
import { Radio, X, Shield } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Turnstile } from "@marsidev/react-turnstile";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const AuthModal = ({ isOpen, onClose, initialMode = 'login', mode: modeProp, defaultPlan = 'starter', onSwitchMode }) => {
  const [mode, setMode] = useState(modeProp || initialMode);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [stationName, setStationName] = useState('');
  const [plan, setPlan] = useState(defaultPlan);
  const [referralCode, setReferralCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const { login, register, loginWithGoogle } = useAuth();

  useEffect(() => {
    setMode(modeProp || initialMode);
    setError('');
    setTurnstileToken('');
    // Check for referral code in URL
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) setReferralCode(ref);
  }, [initialMode, modeProp, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError('Please complete the verification');
      return;
    }

    setLoading(true);

    try {
      const tokenToSend = turnstileToken === 'skip' ? undefined : turnstileToken;
      if (mode === 'login') {
        await login(email, password, tokenToSend);
      } else {
        await register(name, email, password, plan, stationName, tokenToSend, referralCode);
      }
      onClose();
      setName('');
      setEmail('');
      setPassword('');
      setStationName('');
      setPlan(defaultPlan);
      setReferralCode('');
      setTurnstileToken('');
    } catch (err) {
      setError(err.message);
      turnstileRef.current?.reset();
      setTurnstileToken('');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="auth-modal">
        <button onClick={onClose} className="absolute top-3 right-3 text-white/50 hover:text-white transition" data-testid="close-modal-btn">
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-4">
          <div className="flex items-center justify-center mb-2">
            <img src="/logo-header.png" alt="DJsandMCMedia™" className="h-10 w-auto" />
          </div>
          <h2 className="text-lg font-semibold text-white">
            {mode === 'login' ? 'Welcome Back' : 'Start Your Station'}
          </h2>
        </div>

        {/* Google Sign In Button */}
        <button
          onClick={loginWithGoogle}
          className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-100 text-gray-800 font-medium py-2.5 rounded-xl transition mb-4"
          data-testid="google-signin-btn"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <div className="relative mb-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/10"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-gray-900 text-white/50">or</span>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500/50 text-red-300 px-3 py-2 rounded-xl mb-3 text-sm" data-testid="auth-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          {mode === 'register' && (
            <>
              <div>
                <label className="block text-white/70 text-xs mb-1">Your Name</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/30 focus:outline-none focus:border-purple-500 transition"
                  placeholder="John Doe" required data-testid="register-name-input" />
              </div>
              {plan !== 'listener' && (
                <div>
                  <label className="block text-white/70 text-xs mb-1">Station Name</label>
                  <input type="text" value={stationName} onChange={(e) => setStationName(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/30 focus:outline-none focus:border-purple-500 transition"
                    placeholder="My Awesome Radio" data-testid="register-station-input" />
                </div>
              )}
            </>
          )}

          <div>
            <label className="block text-white/70 text-xs mb-1">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/30 focus:outline-none focus:border-purple-500 transition"
              placeholder="you@example.com" required data-testid="auth-email-input" />
          </div>

          <div>
            <label className="block text-white/70 text-xs mb-1">Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/30 focus:outline-none focus:border-purple-500 transition"
              placeholder="••••••••" required minLength={6} data-testid="auth-password-input" />
          </div>

          {mode === 'register' && (
            <div>
              <label className="block text-white/70 text-xs mb-1">Select Plan</label>
              <div className="grid grid-cols-5 gap-2">
                {[
                  { id: 'listener', label: 'Free Listener' },
                  { id: 'starter', label: 'Starter' },
                  { id: 'professional', label: 'Pro' },
                  { id: 'premium', label: 'Premium' },
                  { id: 'byos', label: 'BYOS' },
                ].map((p) => (
                  <button key={p.id} type="button" onClick={() => setPlan(p.id)}
                    className={`py-1.5 px-2 rounded-lg text-xs font-medium transition ${plan === p.id ? p.id === 'listener' ? 'bg-green-500 text-white' : 'bg-purple-500 text-white' : 'bg-white/5 text-white/70 hover:bg-white/10'}`}
                    data-testid={`plan-${p.id}-btn`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {mode === 'register' && (
            <div>
              <label className="block text-white/70 text-xs mb-1">Referral Code (optional)</label>
              <input type="text" value={referralCode} onChange={(e) => setReferralCode(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/30 focus:outline-none focus:border-purple-500 transition"
                placeholder="Enter referral code" data-testid="register-referral-input" />
            </div>
          )}

          {/* Cloudflare Turnstile */}
          {TURNSTILE_SITE_KEY && (
            <div className="flex justify-center py-1" data-testid="turnstile-container">
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={(token) => setTurnstileToken(token)}
                onError={() => setTurnstileToken('skip')}
                onExpire={() => { setTurnstileToken(''); turnstileRef.current?.reset(); }}
                options={{ theme: 'dark', size: 'normal' }}
              />
            </div>
          )}

          <button type="submit" disabled={loading}
            className="w-full bg-purple-500 hover:bg-purple-600 text-white font-semibold py-2.5 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed mt-2"
            data-testid="auth-submit-btn">
            {loading ? 'Please wait...' : (mode === 'login' ? 'Sign In' : 'Create Account')}
          </button>
        </form>

        <div className="flex items-center justify-center gap-1.5 mt-3">
          <Shield className="w-3 h-3 text-white/20" />
          <span className="text-white/20 text-[10px]">Protected by Cloudflare</span>
        </div>

        <p className="text-center text-white/60 text-sm mt-3">
          {mode === 'login' ? (
            <>Don't have an account? <button onClick={() => { setMode('register'); setTurnstileToken(''); turnstileRef.current?.reset(); }} className="text-purple-400 hover:text-purple-300 transition" data-testid="switch-to-register">Sign up</button></>
          ) : (
            <>Already have an account? <button onClick={() => { setMode('login'); setTurnstileToken(''); turnstileRef.current?.reset(); }} className="text-purple-400 hover:text-purple-300 transition" data-testid="switch-to-login">Sign in</button></>
          )}
        </p>
      </div>
    </div>
  );
};

export default AuthModal;
